import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { refreshSession, type User } from '@/features/auth';

type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

type AuthState = {
  status: AuthStatus;
  user: User | null;
  rememberMe: boolean;
  setSession: (user: User, rememberMe: boolean) => void;
  clearSession: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unknown');
  const [user, setUser] = useState<User | null>(null);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    let active = true;
    refreshSession()
      .then((profile) => {
        if (!active) return;
        setUser(profile);
        // rememberMe is unknown after a reload until /auth/tracking/me exposes session type
        // (backend gap, see docs/auth-backend-contract.md). Default false → idle timer applies.
        setRememberMe(false);
        setStatus('authenticated');
      })
      .catch(() => {
        if (active) setStatus('unauthenticated');
      });
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      rememberMe,
      setSession: (u, r) => {
        setUser(u);
        setRememberMe(r);
        setStatus('authenticated');
      },
      clearSession: () => {
        setUser(null);
        setRememberMe(false);
        setStatus('unauthenticated');
      },
    }),
    [status, user, rememberMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
